import React from "react";
import { Eye, CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { KycOverrideButton } from "./KycOverrideButton";
import { DigiLockerBadge, ManualBadge, NameMismatchWarning } from "./KycSourceBadges";

export const STATUS_PILL = {
  pending_review: { cls: "bg-amber-100 text-amber-800", label: "Pending" },
  approved: { cls: "bg-emerald-100 text-emerald-800", label: "Approved" },
  rejected: { cls: "bg-rose-100 text-rose-800", label: "Rejected" },
};

export const DOC_LABEL = {
  aadhaar: "Aadhaar",
  address_proof: "Address Proof",
  photo: "Photo",
  degree: "Degree",
  bci_cert: "BCI Certificate",
  pan: "PAN",
  bank_account: "Bank account",
  upi_id: "UPI ID",
  gst: "GST number",
  other: "Other",
};

const VERIFICATION_LABEL = { sandbox_verified: "NSDL verified", manual_review: "Manual review" };

function FinancialDetails({ item }) {
  const fin = item.financial;
  const holder = fin.verification_details?.holder_name_match;
  const nsdlName = fin.verification_details?.name_on_pan;
  return (
    <div className="mt-1" data-testid={`kyc-financial-${item.id}`}>
      <p className="text-sm text-[#0A2540]">
        First-time submission: <strong data-testid={`kyc-financial-value-${item.id}`}>{fin.masked}</strong>
        {item.has_file === false && <span className="ml-2 text-[11px] text-slate-400">· no file attached</span>}
      </p>
      {fin.verification_status && (
        <p className="text-[11px] mt-1">
          <span className="text-slate-500">external verification: </span>
          <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${fin.verification_status === "sandbox_verified" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid={`kyc-financial-verification-${item.id}`}>
            {VERIFICATION_LABEL[fin.verification_status] || fin.verification_status.replace(/_/g, " ")}
          </span>
          {nsdlName && <span className="ml-2 text-slate-500">NSDL name: <strong className="text-[#0A2540]">{nsdlName}</strong></span>}
        </p>
      )}
      {holder?.status === "mismatch" && (
        <div className="mt-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs" role="alert" data-testid={`kyc-holder-mismatch-${item.id}`}>
          <p className="font-bold inline-flex items-center gap-1"><AlertTriangle size={12} /> Holder name mismatch</p>
          <p className="mt-1">Holder: <strong>{holder.holder_name}</strong> · compared to {holder.compared_to_source?.replace(/_/g, " ")}: <strong>{holder.compared_to_name || "—"}</strong></p>
        </div>
      )}
      {holder?.status === "matched" && (
        <p className="text-[11px] text-emerald-700 mt-1" data-testid={`kyc-holder-matched-${item.id}`}>Holder name matches {holder.compared_to_source?.replace(/_/g, " ")} ({holder.compared_to_name})</p>
      )}
    </div>
  );
}

export function KycDocRow({ item, busy, onView, onApprove, onReject, onOverride }) {
  const pill = STATUS_PILL[item.status] || { cls: "bg-slate-100 text-slate-700", label: item.status };
  const isDigiLocker = item.source === "digilocker";
  const dl = item.digilocker || {};
  return (
    <li className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`kyc-row-${item.id}`}>
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm font-bold text-[#0A2540] flex flex-wrap items-center gap-2" data-testid={`kyc-doc-type-${item.id}`}>
          {DOC_LABEL[item.doc_type] || item.doc_type.replace("_", " ")}
          {isDigiLocker ? <DigiLockerBadge id={item.id} /> : <ManualBadge id={item.id} />}
        </p>
        <p className="text-xs text-slate-500">
          {isDigiLocker
            ? `Issued by ${dl.issuer || "authority"} · fetched ${dl.fetched_at?.slice(0, 16)?.replace("T", " ") || item.uploaded_at?.slice(0, 16)?.replace("T", " ")}`
            : item.has_file === false
            ? `Submitted ${item.uploaded_at?.slice(0, 16)?.replace("T", " ")}`
            : `${item.filename} · ${((item.size || 0) / 1024).toFixed(1)} KB · ${item.uploaded_at?.slice(0, 16)?.replace("T", " ")}`}
        </p>
        {item.financial && <FinancialDetails item={item} />}
        {item.financial && item.note && <p className="mt-1 text-xs text-slate-600 italic">&ldquo;{item.note}&rdquo;</p>}
        {isDigiLocker && dl.name_match_status === "mismatch" && (
          <div className="mt-2">
            <NameMismatchWarning id={item.id} documentName={dl.name} registeredName={dl.registered_name} compact />
          </div>
        )}
        {isDigiLocker && dl.name_match_status === "matched" && (
          <p className="text-[11px] text-emerald-700 mt-1" data-testid={`kyc-name-matched-${item.id}`}>Name matches registered name ({dl.name})</p>
        )}
        {isDigiLocker && item.doc_type === "pan" && item.pan_number_readable === false && (
          <div className="mt-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs" role="alert" data-testid={`kyc-pan-unreadable-${item.id}`}>
            <p className="font-bold inline-flex items-center gap-1"><AlertTriangle size={12} /> PAN number not readable</p>
            <p className="mt-1">This DigiLocker file ({item.filename}) is not the signed XML record, so approving it will not set the lawyer's PAN. Ask the lawyer to fetch PAN from DigiLocker again or enter it manually.</p>
          </div>
        )}
        {item.review_note && <p className="text-xs text-rose-600 mt-1">Note: {item.review_note}</p>}
        {!isDigiLocker && /^attachment for (pan|bank_account|upi_id|gst) change/i.test(item.note || "") && (
          <p className="text-[11px] text-sky-700 mt-1" data-testid={`kyc-attachment-hint-${item.id}`}>
            Supporting file for a {item.note.split(" ")[2].replace("_", " ")} change request — decide the request itself under Approvals → KYC Changes.
          </p>
        )}
      </div>
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${pill.cls}`}
        data-testid={`kyc-doc-status-${item.id}`}
      >
        {pill.label}
      </span>
      <div className="flex flex-wrap gap-1">
        {item.has_file !== false && (
        <Button
          variant="outline" size="sm" onClick={() => onView(item)}
          disabled={busy === `view-${item.id}`} data-testid={`kyc-view-${item.id}`}
          className="rounded-lg text-xs h-8"
        >
          {busy === `view-${item.id}` ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} className="mr-1" />} View
        </Button>
        )}
        {item.status === "pending_review" && (
          <Button
            size="sm" onClick={() => onApprove(item)} disabled={busy === `ok-${item.id}`}
            data-testid={`kyc-approve-${item.id}`}
            className="rounded-lg text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {busy === `ok-${item.id}` ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} className="mr-1" />} Approve
          </Button>
        )}
        {item.status === "pending_review" && (
          <Button
            size="sm" variant="destructive" onClick={() => onReject(item)} disabled={busy === `no-${item.id}`}
            data-testid={`kyc-reject-${item.id}`} className="rounded-lg text-xs h-8"
          >
            {busy === `no-${item.id}` ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} className="mr-1" />} Reject
          </Button>
        )}
        {item.status !== "pending_review" && (
          <KycOverrideButton item={item} busy={busy} onConfirm={onOverride} />
        )}
      </div>
    </li>
  );
}
